import React from 'react';

interface IconProps {
  d: string;
  size?: number;
  stroke?: number;
  fill?: string;
  className?: string;
}

export const Icon: React.FC<IconProps> = ({ d, size = 14, stroke = 1.5, fill = 'none', className }) => (
  <svg
    className={`sh-ico ${className ?? ''}`.trim()}
    viewBox="0 0 24 24"
    width={size}
    height={size}
    fill={fill}
    stroke="currentColor"
    strokeWidth={stroke}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d={d} />
  </svg>
);

export const ICONS = {
  arrowRight: 'M5 12h14M13 6l6 6-6 6',
  arrowUpRight: 'M7 17 17 7M9 7h8v8',
  copy: 'M9 9h10v10H9zM5 5h10v4',
  external: 'M14 5h5v5M19 5l-9 9M5 5h6v0M5 11v8h8',
  check: 'M5 12l4 4 10-10',
  download: 'M12 4v12M7 11l5 5 5-5M4 20h16',
  filter: 'M4 6h16M7 12h10M10 18h4',
  search: 'M11 4a7 7 0 1 1 0 14 7 7 0 0 1 0-14zm9 16-4-4',
  shield: 'M12 3 4 6v6c0 5 4 8 8 9 4-1 8-4 8-9V6l-8-3z',
  chevron: 'M9 6l6 6-6 6',
  caret: 'M6 9l6 6 6-6',
  clock: 'M12 7v5l3 2M12 21a9 9 0 1 1 0-18 9 9 0 0 1 0 18z',
  link: 'M10 14a5 5 0 0 0 7 0l3-3a5 5 0 0 0-7-7l-1 1M14 10a5 5 0 0 0-7 0l-3 3a5 5 0 0 0 7 7l1-1',
  plus: 'M12 5v14M5 12h14',
  close: 'M6 6l12 12M6 18 18 6',
  info: 'M12 8v.01M11 12h1v5M12 3a9 9 0 1 1 0 18 9 9 0 0 1 0-18z',
  warn: 'M12 9v2m0 4h.01M5.062 21h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z',
  power: 'M12 4v8M5 8a8 8 0 1 0 14 0',
  wallet: 'M3 7a2 2 0 0 1 2-2h14v4H5a2 2 0 0 1-2-2zM3 7v10a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-6H5a2 2 0 0 1-2-2zM18 13.5a.5.5 0 1 0 0-1 .5.5 0 0 0 0 1z',
};

interface SunburstProps {
  size?: number;
  color?: string;
}

export const SunburstMark: React.FC<SunburstProps> = ({ size = 26, color = 'currentColor' }) => {
  const cx = 12;
  const cy = 18;
  const r = 13;
  const w = 1.6;
  const rays: React.ReactNode[] = [];
  for (let i = 0; i < 9; i++) {
    const angle = ((-180 + i * 22.5) * Math.PI) / 180;
    const x2 = cx + Math.cos(angle) * r;
    const y2 = cy + Math.sin(angle) * r;
    rays.push(
      <line
        key={i}
        x1={cx}
        y1={cy}
        x2={x2}
        y2={y2}
        stroke={color}
        strokeWidth={w}
        strokeLinecap="square"
      />,
    );
  }
  return (
    <svg width={size} height={size * 0.7} viewBox="0 0 24 20" aria-hidden="true">
      {rays}
    </svg>
  );
};

interface LogoProps {
  wordmark?: boolean;
}

export const Logo: React.FC<LogoProps> = ({ wordmark = true }) => (
  <div className="sh-logo">
    <SunburstMark size={26} color="var(--text)" />
    {wordmark && (
      <span style={{ fontWeight: 500, letterSpacing: '-0.01em' }}>
        Sahara <span style={{ color: 'var(--text-2)' }}>· Vesting</span>
      </span>
    )}
  </div>
);

export const DuneLines: React.FC<{ width?: number; height?: number; style?: React.CSSProperties }> = ({
  width = 520,
  height = 220,
  style,
}) => {
  const lines: React.ReactNode[] = [];
  for (let i = 0; i < 22; i++) {
    const y = 8 + i * 8;
    const amp = 20 - Math.abs(i - 11) * 1.4;
    lines.push(
      <path
        key={i}
        d={`M0 ${y} Q 240 ${y - amp}, 480 ${y}`}
        stroke="var(--text)"
        strokeWidth="0.6"
        fill="none"
        opacity={0.05 + (i < 11 ? i : 22 - i) * 0.02}
      />,
    );
  }
  return (
    <svg
      className="sh-dune-lines"
      viewBox="0 0 480 200"
      preserveAspectRatio="none"
      style={{ width, height, ...style }}
    >
      {lines}
    </svg>
  );
};
